import auth from '@/auth/auth';
import {
    CreateExternalEventLocationDocument,
    CreateMultiLinkDocument,
    DeleteExternalEventLocationDocument,
    DeleteExternalEventSeriesDocument,
    GetExternalEventSeriesDataDocument,
    UpdateExternalEventSeriesDocument,
    UpdateExternalEventSeriesMultiLinksDocument,
    UpdateExternalEventSeriesMutationVariables,
} from '@/generated/graphql';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { computed, Ref, toValue } from 'vue';
import { useRouter } from 'vue-router';
import { useExternalEventVisibilities } from './useExternalEventVisibilities';

const DEFAULT_EXTERNAL_EVENT_SERIES_STATUS = 'active';

export function useExternalEventSeries(seriesId: Ref<string>) {
    const isAdmin = auth.isAdmin();
    const fluent = useFluent();
    const router = useRouter();

    const {
        result: getSeriesDataQuery,
        loading,
        refetch,
    } = useQuery(
        GetExternalEventSeriesDataDocument,
        () => ({ id: toValue(seriesId) }),
        () => ({
            enabled: !!toValue(seriesId),
        }),
    );
    const series = computed(() => getSeriesDataQuery.value?.externalEventSeries ?? undefined);
    const seriesStatus = computed(() => series.value?.status ?? DEFAULT_EXTERNAL_EVENT_SERIES_STATUS);

    const deleteExternalEventSeriesMutation = useMutation(DeleteExternalEventSeriesDocument);
    const deleteDisabledExplanation = computed(() => {
        if (!isAdmin) return fluent.$t('no-permission-to-edit');
        if (series.value?.events && series.value?.events.totalCount > 0)
            return fluent.$t('external-event-series-has-events');
        return undefined;
    });

    async function deleteSeries() {
        if (!isAdmin) return;
        if (!series.value) return;
        if (!confirm(fluent.$t('confirm-delete-external-event-series', { name: series.value.nameShort ?? '' }))) return;

        const response = await deleteExternalEventSeriesMutation.mutate({ id: seriesId.value });
        if (response?.errors) {
            throw new Error(`Could not delete external event series ${response.errors}`);
        }
        if (response?.data) {
            router.replace({ name: 'external-events' });
        }
    }

    const createEventDisabledExplanation = computed(() => {
        if (!isAdmin) {
            return fluent.$t('no-permission-to-create');
        }
    });

    const updateMutation = useMutation(UpdateExternalEventSeriesDocument);
    const deleteLocationMutation = useMutation(DeleteExternalEventLocationDocument);
    const createLocationMutation = useMutation(CreateExternalEventLocationDocument);

    const {
        createDefaultVisibilities,
        createDefaultVisibilitiesForFederalStates,
        deleteDefaultVisibilitiesForFederalStates,
    } = useExternalEventVisibilities();

    async function externalEventSeriesUpdate(data: UpdateExternalEventSeriesMutationVariables) {
        if (series.value) {
            // Change in national value!
            if (series.value.national !== data.national) {
                const events = series.value.events.nodes;
                const eventIds = events.map((e) => e.id);
                if (data.national) {
                    // change from international (single location)
                    // to national (federal states locations)
                    const locationIds = series.value.events.nodes
                        .map((n) => n.locationId)
                        .filter((id) => id !== undefined && id !== null);
                    for (const id of locationIds) {
                        await deleteLocationMutation.mutate({ id });
                    }
                    for (const id of eventIds) {
                        await createDefaultVisibilitiesForFederalStates(id);
                    }
                } else {
                    // change from national (federal state locations)
                    // to international (single location with single visibility)
                    for (const event of events) {
                        try {
                            await deleteDefaultVisibilitiesForFederalStates(event.id);
                        } catch (error) {
                            return { data: undefined };
                        }

                        const createLocationResult = await createLocationMutation.mutate({
                            eventId: event.id,
                            locationName: event.nameShort,
                        });

                        const createdLocationId =
                            createLocationResult?.data?.updateExternalEvent?.externalEvent?.locationId;
                        if (!createdLocationId) {
                            throw Error('Could not create default location for event');
                        }

                        await createDefaultVisibilities(event.id, [createdLocationId]);
                    }
                }
            }

            // save series
            const result = await updateMutation.mutate(data);
            if (result?.errors) {
                return { errors: result.errors };
            }
            refetch();
            return { data: result?.data ?? undefined };
        }
        return { data: undefined };
    }

    const updateSeriesMultiLinksMutation = useMutation(UpdateExternalEventSeriesMultiLinksDocument);
    async function updateExternalEventSeriesMultiLinkId(fieldName: string, multiLinkId: string) {
        if (!series.value) return;
        const response = await updateSeriesMultiLinksMutation.mutate({
            id: series.value.id,
            [fieldName]: multiLinkId,
        });
        if (response?.errors) {
            throw new Error(`Could not update project multilink ${fieldName}: ${response.errors}`);
        }
    }

    const createMultiLinkMutation = useMutation(CreateMultiLinkDocument);
    async function createExternalEventSeriesMultiLink(fieldName: string) {
        if (!series.value) return;
        const name = `${series.value.id}-${fieldName}`;
        const response = await createMultiLinkMutation.mutate({ name });
        if (response?.errors) {
            throw new Error(`Could not create Multilink for external event series: ${response.errors}`);
        }
        if (response?.data) {
            const multiLinkId = response.data.createMultilink?.multilink?.id;
            if (multiLinkId) {
                await updateExternalEventSeriesMultiLinkId(`${fieldName}Id`, multiLinkId);
                return multiLinkId;
            }
        }
    }

    return {
        series,
        loading,
        seriesStatus,
        deleteDisabledExplanation,
        createEventDisabledExplanation,
        deleteSeries,
        externalEventSeriesUpdate,
        createExternalEventSeriesMultiLink,
    };
}
